/**
 * Client-side Image Compression Utility for Social Baazar Admin Panel
 * Resizes large images to max 1920x1920 and compresses them using HTML5 canvas.
 * Preserves videos, GIFs, and non-image files untouched.
 */

export const compressImageFile = async (file, maxWidth = 1920, maxHeight = 1920, quality = 0.82) => {
    if (!file || !file.type || !file.type.startsWith("image/") || file.type === "image/svg+xml" || file.type === "image/gif") {
        return file;
    }

    // Skip if already smaller than 120 KB
    if (file.size < 120 * 1024) {
        return file;
    }

    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    if (width > height) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    } else {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);

                // Use JPEG for PNG or other lossy images to get significant compression while keeping crystal clear visuals
                const outputType = file.type === "image/png" ? "image/jpeg" : (file.type || "image/jpeg");

                canvas.toBlob(
                    (blob) => {
                        if (!blob || blob.size >= file.size) {
                            // If compression didn't reduce size, keep original
                            resolve(file);
                        } else {
                            const compressedFile = new File([blob], file.name.replace(/\.png$/i, '.jpg'), {
                                type: outputType,
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        }
                    },
                    outputType,
                    quality
                );
            };
            img.onerror = () => resolve(file);
            img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
    });
};

export const compressFilesBatch = async (items, onProgress) => {
    if (!items || items.length === 0) {
        return {
            compressedItems: [],
            stats: { originalTotal: 0, compressedTotal: 0, savedBytes: 0, savedPercent: 0 }
        };
    }

    let originalTotal = 0;
    let compressedTotal = 0;
    const compressedItems = [];

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const rawFile = item.file;
        const rawSize = rawFile?.size || 0;
        originalTotal += rawSize;

        let compFile = rawFile;
        try {
            compFile = await compressImageFile(rawFile);
        } catch (err) {
            console.error("Image compression error for:", rawFile?.name, err);
            compFile = rawFile;
        }

        const compSize = compFile?.size || 0;
        compressedTotal += compSize;

        compressedItems.push({
            ...item,
            file: compFile,
            originalSize: rawSize,
            compressedSize: compSize
        });

        if (onProgress) {
            const pct = Math.round(((i + 1) / items.length) * 100);
            onProgress(pct, i + 1, items.length);
        }
    }

    const savedBytes = Math.max(0, originalTotal - compressedTotal);
    const savedPercent = originalTotal > 0 ? Math.round((savedBytes / originalTotal) * 100) : 0;

    return {
        compressedItems,
        stats: {
            originalTotal,
            compressedTotal,
            savedBytes,
            savedPercent
        }
    };
};

